/**
 * ArcheDataSection — My Data's "Arche data" (file areas §4.9): what arches
 * keep for the user OUTSIDE their files, so no store they rely on or pay for
 * is invisible.
 *
 *   - sandbox storage — what each arche saved in its private storage;
 *   - DataStore — the user's own collections in each arche;
 *   - plugin data the user pays for — their own, their workspaces' and their
 *     apps' — against their plan's allowance (ADR-032);
 *   - game state — keywords, and the arches granted them;
 *   - Vault data — notes, calendar, email and the rest, by data class, and
 *     what that storage cost this month.
 *
 * View-only in v1 (decided §9 Q5). Fetched when opened.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, Database } from 'lucide-react';
import type { VaultArcheDataEntryDto } from '@/lib/api';
import { formatBytes } from '@/components/files/file-dialog/format';
import { scrubForDisplay } from '@/components/files/file-dialog/display-safety';
import { useVaultArcheData } from '../hooks/useVaultDocuments';

/** "calendar_events" → "Calendar events". */
function dataClassLabel(dataClass: string): string {
  const words = dataClass.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function archeLabel(entry: VaultArcheDataEntryDto): string {
  return entry.archeName
    ? scrubForDisplay(entry.archeName, 60)
    : 'An arche that’s no longer available';
}

function ArcheRow({ entry }: { entry: VaultArcheDataEntryDto }) {
  const parts: string[] = [];
  if (entry.sandboxStorage) {
    parts.push(
      `Storage: ${entry.sandboxStorage.objectCount} ${entry.sandboxStorage.objectCount === 1 ? 'item' : 'items'}, ${formatBytes(entry.sandboxStorage.bytes)}`,
    );
  }
  if (entry.dataStore) {
    const docs = entry.dataStore.collections.reduce((n, c) => n + c.documentCount, 0);
    parts.push(
      `Data: ${docs} ${docs === 1 ? 'record' : 'records'} in ${entry.dataStore.collections.length} ${entry.dataStore.collections.length === 1 ? 'collection' : 'collections'}, ${formatBytes(entry.dataStore.bytes)}`,
    );
    if (entry.dataStore.workspaceCount > 0) {
      parts.push(
        `${entry.dataStore.workspaceCount} shared ${entry.dataStore.workspaceCount === 1 ? 'workspace' : 'workspaces'}`,
      );
    }
  }
  if (entry.gameState) {
    parts.push(
      `Game state: ${entry.gameState.keywordCount} ${entry.gameState.keywordCount === 1 ? 'entry' : 'entries'}, ${formatBytes(entry.gameState.bytes)}`,
    );
  }
  return (
    <li className="flex flex-col gap-0.5 border-b border-border py-2 last:border-b-0">
      <span className="font-medium text-foreground">{archeLabel(entry)}</span>
      {parts.length > 0 && (
        <span className="text-xs text-muted-foreground">{parts.join(' · ')}</span>
      )}
      {entry.dataStoreUnavailable && (
        <span className="text-xs text-amber-700 dark:text-amber-400">
          Its saved data couldn’t be read just now.
        </span>
      )}
    </li>
  );
}

export function ArcheDataSection({ holderId }: { holderId: string | null }) {
  const [open, setOpen] = useState(false);
  const query = useVaultArcheData(holderId, open);
  const data = query.data;

  return (
    <section
      className="rounded border border-border bg-card p-4"
      aria-labelledby="arche-data-heading"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
        )}
        <Database className="h-4 w-4 text-muted-foreground" aria-hidden />
        <span id="arche-data-heading" className="text-sm font-medium text-foreground">
          Arche data
        </span>
        <span className="text-xs text-muted-foreground">
          — what arches keep for you besides files
        </span>
      </button>

      {open && query.isLoading && <p className="mt-3 text-xs text-muted-foreground">Looking…</p>}
      {open && query.isError && (
        <p role="alert" className="mt-3 text-xs text-destructive">
          Couldn’t load your arche data. Try again later.
        </p>
      )}
      {open && data && (
        <div className="mt-3 flex flex-col gap-4 text-sm">
          {data.arches.length === 0 ? (
            <p className="text-xs text-muted-foreground">No arche keeps data for you yet.</p>
          ) : (
            <ul>
              {data.arches.map((entry) => (
                <ArcheRow key={entry.archeId} entry={entry} />
              ))}
            </ul>
          )}
          {data.dataStoreUnavailable && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              What arches have saved in their databases couldn’t be read just now, so it isn’t
              listed here. Try again later.
            </p>
          )}
          {data.truncatedDataStoreArches > 0 && (
            <p className="text-xs text-muted-foreground">
              {data.truncatedDataStoreArches} more{' '}
              {data.truncatedDataStoreArches === 1 ? 'arche keeps' : 'arches keep'} saved data not
              shown here.
            </p>
          )}
          {data.pluginData && (
            <p className="text-xs text-muted-foreground">
              Plugin data you pay for: {formatBytes(data.pluginData.usedBytes)} of the{' '}
              {formatBytes(data.pluginData.allowanceBytes)} your plan allows. It is billed with your
              database storage.
            </p>
          )}
          {data.gameState.keywordCount > 0 && (
            <p className="text-xs text-muted-foreground">
              All game state: {data.gameState.keywordCount}{' '}
              {data.gameState.keywordCount === 1 ? 'entry' : 'entries'},{' '}
              {formatBytes(data.gameState.bytes)}.
            </p>
          )}

          {data.vaultData && (
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Vault data in this workspace
              </h3>
              {data.vaultData.byClass.length === 0 ? (
                <p className="mt-1 text-xs text-muted-foreground">None yet.</p>
              ) : (
                <ul className="mt-1 flex flex-col gap-0.5">
                  {data.vaultData.byClass.map((c) => (
                    <li key={c.dataClass} className="flex justify-between gap-3 text-xs">
                      <span className="text-foreground">{dataClassLabel(c.dataClass)}</span>
                      <span className="text-muted-foreground">
                        {c.rowCount.toLocaleString()} {c.rowCount === 1 ? 'item' : 'items'} ·{' '}
                        {formatBytes(c.bytes)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Total {formatBytes(data.vaultData.totalBytes)}
                {data.vaultData.chargedThisMonthCredits !== null &&
                  ` · charged this month: ${data.vaultData.chargedThisMonthCredits.toFixed(2)} credits`}
              </p>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            This is a view of what’s kept. To remove an arche’s data, use the arche itself.
          </p>
        </div>
      )}
    </section>
  );
}
